/**
 * Expectation List component
 * Supports pagination for large lists
 */

import { useState, useMemo } from 'react'
import { ExpectationCard } from './ExpectationCard'
import { Pagination } from '../common/Pagination'
import { Spinner } from '@/components/ui/spinner'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { IconTarget } from '@tabler/icons-react'
import { useGoldPrices } from '@/services/api/goldPrice'
import type { PriceExpectation } from '@/types/expectation'

interface ExpectationListProps {
  expectations: PriceExpectation[]
  onEdit?: (expectation: PriceExpectation) => void
  onDelete?: (expectation: PriceExpectation) => void
  isLoading?: boolean
  itemsPerPage?: number
}

export function ExpectationList({
  expectations,
  onEdit,
  onDelete,
  isLoading,
  itemsPerPage = 12,
}: ExpectationListProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const { data: currentPrices } = useGoldPrices('Hồ Chí Minh')

  // Create map of current prices by gold type (using first price as default)
  const defaultPrice = currentPrices?.[0]?.SellValue

  const paginatedExpectations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return expectations.slice(startIndex, endIndex)
  }, [expectations, currentPage, itemsPerPage])

  const totalPages = Math.ceil(expectations.length / itemsPerPage)

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Spinner className="h-12 w-12 mx-auto mb-4" />
        <p className="text-muted-foreground">Đang tải kỳ vọng...</p>
      </div>
    )
  }

  if (expectations.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <IconTarget className="h-12 w-12" stroke={1.5} />
          </EmptyMedia>
          <EmptyTitle>Chưa có kỳ vọng giá nào</EmptyTitle>
          <EmptyDescription>
            Hãy tạo kỳ vọng giá đầu tiên của bạn để theo dõi mục tiêu đầu tư
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedExpectations.map((expectation) => (
          <ExpectationCard
            key={expectation.id}
            expectation={expectation}
            {...(defaultPrice !== undefined ? { currentPrice: defaultPrice } : {})}
            {...(onEdit ? { onEdit } : {})}
            {...(onDelete ? { onDelete } : {})}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={expectations.length}
        />
      )}
    </>
  )
}
